import { useRef } from "react";
import CountUp from "react-countup";
import { motion, useInView } from "framer-motion";
import { ArrowUpRight, Clock, Heart, Rocket, Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
import { useHorizontalRail } from "./rail/useHorizontalRail";
import {
    RailArrows,
    RailEdgeFades,
    RailEyebrow,
    RailPagination,
    RailProgressBar,
    RailScrollbarHideStyle,
    railContainerClass,
} from "./rail/RailControls";

type StatKey = "satisfaction" | "years" | "projects" | "clients";

interface StatDef {
    num: string;
    key: StatKey;
    end: number;
    suffix: string;
    icon: React.ReactNode;
    accentHue: number;
}

const STATS: StatDef[] = [
    { num: "01", key: "satisfaction", end: 99, suffix: "%", icon: <Heart className="w-7 h-7" />, accentHue: 260 },
    { num: "02", key: "years", end: 7, suffix: "+", icon: <Clock className="w-7 h-7" />, accentHue: 190 },
    { num: "03", key: "projects", end: 45, suffix: "+", icon: <Rocket className="w-7 h-7" />, accentHue: 320 },
    { num: "04", key: "clients", end: 102, suffix: "+", icon: <Users className="w-7 h-7" />, accentHue: 160 },
];

const StatCard = ({
    stat,
    index,
    total,
    staggerInView,
    countInView,
}: {
    stat: StatDef;
    index: number;
    total: number;
    staggerInView: boolean;
    countInView: boolean;
}) => {
    const { t } = useTranslation();
    const accent = `hsl(${stat.accentHue} 90% 60%)`;
    const accentDim = `hsl(${stat.accentHue} 90% 60% / 0.35)`;
    const accentSoft = `hsl(${stat.accentHue} 90% 60% / 0.18)`;

    return (
        <motion.div
            className="group relative h-full"
            initial={{ opacity: 0, y: 30 }}
            animate={staggerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{
                duration: 0.55,
                delay: 0.08 * index,
                ease: [0.22, 1, 0.36, 1],
            }}
            whileHover={{ y: -4 }}
        >
            <div
                aria-hidden
                className="absolute -inset-px rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                    background: `conic-gradient(from 140deg at 50% 50%, ${accent}, transparent 35%, transparent 65%, ${accent})`,
                    padding: "1px",
                    WebkitMask:
                        "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                    WebkitMaskComposite: "xor",
                    mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                    maskComposite: "exclude",
                }}
            />

            <div
                className="glass-v2 rounded-[32px] p-7 md:p-8 relative overflow-hidden h-full flex flex-col"
                data-cursor="hover"
            >
                <span
                    aria-hidden
                    className="absolute -top-4 end-4 font-v2-display leading-none select-none pointer-events-none text-[120px] md:text-[140px] text-white/[0.04] tracking-tighter"
                >
                    {stat.num}
                </span>

                <div
                    aria-hidden
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{
                        background: `radial-gradient(600px circle at 50% 0%, ${accentSoft}, transparent 60%)`,
                    }}
                />

                <div
                    aria-hidden
                    className="absolute top-0 inset-x-8 h-px opacity-60 pointer-events-none"
                    style={{
                        background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
                    }}
                />

                <div className="relative flex-1 flex flex-col">
                    <div className="flex items-start gap-4 mb-5">
                        <div className="relative shrink-0">
                            <div
                                aria-hidden
                                className="absolute -inset-1.5 rounded-[20px] opacity-0 group-hover:opacity-60 transition-opacity duration-500 blur-md"
                                style={{ background: accent }}
                            />
                            <div
                                className="relative w-14 h-14 rounded-2xl flex items-center justify-center text-white transition-transform duration-500 group-hover:scale-110"
                                style={{
                                    background: `linear-gradient(145deg, ${accent}, hsl(${stat.accentHue} 90% 45%))`,
                                    boxShadow: `0 0 32px ${accentSoft}, inset 0 1px 0 hsl(${stat.accentHue} 90% 80% / 0.4)`,
                                }}
                            >
                                {stat.icon}
                            </div>
                        </div>

                        <div className="flex-1 min-w-0 pt-1">
                            <div className="flex items-center gap-2 mb-1.5">
                                <span
                                    className="font-v2-label text-[9px] px-2 py-0.5 rounded-full border"
                                    style={{
                                        background: accentSoft,
                                        borderColor: accentDim,
                                        color: `hsl(${stat.accentHue} 90% 85%)`,
                                    }}
                                >
                                    {stat.num}
                                </span>
                                <span className="font-v2-label text-[10px] text-white/40">
                                    {t("stats_strip.eyebrow")}
                                </span>
                            </div>
                            <p className="font-v2-display text-white text-4xl md:text-5xl leading-none">
                                {countInView ? (
                                    <CountUp end={stat.end} duration={1.8} useEasing suffix={stat.suffix} />
                                ) : (
                                    <span>0{stat.suffix}</span>
                                )}
                            </p>
                        </div>
                    </div>

                    <h3 className="font-v2-display text-lg md:text-xl text-white leading-tight mb-2">
                        {t(`stats_strip.${stat.key}`)}
                    </h3>
                    <p className="text-sm md:text-[15px] text-muted-foreground leading-relaxed font-medium">
                        {t(`stats_strip.${stat.key}_caption`)}
                    </p>

                    <div className="mt-auto pt-6 flex items-center justify-between">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <span className="font-v2-label text-[10px] text-white/50 tabular-nums">
                                {String(index + 1).padStart(2, "0")}
                                <span className="text-white/20 mx-1">/</span>
                                {String(total).padStart(2, "0")}
                            </span>
                            <div className="relative h-px w-10 group-hover:w-20 transition-[width] duration-500 overflow-hidden">
                                <div className="absolute inset-0 bg-white/10" />
                                <div
                                    className="absolute inset-y-0 start-0 w-0 group-hover:w-full transition-[width] duration-700 ease-out"
                                    style={{ background: accent }}
                                />
                            </div>
                        </div>
                        <div
                            className="w-9 h-9 rounded-full flex items-center justify-center text-white transition-all duration-500 group-hover:scale-110 group-hover:rotate-45"
                            style={{
                                background: accentSoft,
                                border: `1px solid ${accentDim}`,
                            }}
                        >
                            <ArrowUpRight size={14} />
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

const StatsStrip = () => {
    const { t } = useTranslation();
    const { dir } = useLanguage();
    const isRtl = dir === "rtl";
    const sectionRef = useRef<HTMLElement>(null);
    const countInView = useInView(sectionRef, { once: true, margin: "-80px" });
    const staggerInView = useInView(sectionRef, { amount: 0.15, margin: "-40px" });

    const {
        railRef,
        canPrev,
        canNext,
        needsScroll,
        activeIndex,
        scrollProgress,
        scrollToIndex,
        scrollByCard,
    } = useHorizontalRail({
        itemCount: STATS.length,
        cardSelector: "[data-stat-card]",
        isRtl,
    });

    return (
        <section ref={sectionRef} className="py-20 md:py-32 relative">
            <div className="container mx-auto px-6 relative">
                <motion.div
                    className="max-w-3xl mx-auto text-center mb-12 md:mb-16"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ amount: 0.3, margin: "-80px" }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                    <RailEyebrow>{t("stats_strip.eyebrow")}</RailEyebrow>
                    <h2 className="font-v2-display text-4xl md:text-6xl text-white mb-6">
                        {t("stats_strip.title_prefix")}{" "}
                        <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                            {t("stats_strip.title_highlight")}
                        </span>
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                        {t("stats_strip.subtitle")}
                    </p>
                </motion.div>

                <RailPagination
                    itemCount={STATS.length}
                    activeIndex={activeIndex}
                    onSelect={scrollToIndex}
                    accentHues={STATS.map((s) => s.accentHue)}
                    ariaLabelPrefix="stat"
                />
            </div>

            <motion.div
                className="relative"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ amount: 0.15, margin: "-40px" }}
                transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
                {needsScroll && (
                    <>
                        <RailEdgeFades isRtl={isRtl} />
                        <RailArrows
                            onPrev={() => scrollByCard(-1)}
                            onNext={() => scrollByCard(1)}
                            canPrev={canPrev}
                            canNext={canNext}
                            isRtl={isRtl}
                        />
                    </>
                )}

                <div
                    ref={railRef}
                    tabIndex={needsScroll ? 0 : -1}
                    aria-label="Stats carousel"
                    className={railContainerClass(needsScroll)}
                >
                    {STATS.map((stat, i) => (
                        <div
                            key={stat.key}
                            data-stat-card
                            className="snap-center shrink-0 w-[85vw] sm:w-[60vw] md:w-[300px] xl:w-[285px] 2xl:w-[330px]"
                        >
                            <StatCard
                                stat={stat}
                                index={i}
                                total={STATS.length}
                                staggerInView={staggerInView}
                                countInView={countInView}
                            />
                        </div>
                    ))}
                </div>

                {needsScroll && <RailProgressBar progress={scrollProgress} />}
            </motion.div>

            <RailScrollbarHideStyle />
        </section>
    );
};

export default StatsStrip;
